import { FC, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import AsyncSelect from "react-select/async";
import { createLoadOptions } from "../../hooks/useDebounce";
import { useI18n } from "../../hooks/useTranslations";
import { useAdminCarsApi } from "../../http/cars/api";
import { Car, CarNoCarDepency } from "../../http/cars/types";
import { HighwayPass } from "../../http/highwayPasses/types";
import { getDefaultDateForm } from "../../types/utils";
import DatePicker from "../../ui/DatePicker";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Input from "../ui/Input";
import reactSelectStyles from "../ui/ReactSelectUtils";

const CAR_FIELDS = ["id", "vehicleIdentificationNumber", "currentPlateNumber", "lastPlateNumber"] as const;
type CarField = (typeof CAR_FIELDS)[number];
type CarSearch = Pick<CarNoCarDepency, CarField>;

export interface HighwayPassFormBody {
  id: string;
  car: {
    id: string;
    vehicleIdentificationNumber: string;
    currentPlateNumber?: string;
    lastPlateNumber?: string;
  };
  startDate: string;
  endDate?: string;
  nr: string;
  prevEndDate?: string;
}

type HighwayPassFormProps = {
  highwayPass?: HighwayPass | null;
  onSubmit: (form: HighwayPassFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
  allowSelectCar?: boolean;
  isCarOverview?: boolean;
};

const HighwayPassForm: FC<HighwayPassFormProps> = ({
  highwayPass,
  onSubmit,
  isDisabled,
  isLoading,
  allowSelectCar,
  isCarOverview,
}: HighwayPassFormProps) => {
  const i18n = useI18n();
  const carsApi = useAdminCarsApi();

  const [defaultCarOptions, setDefaultCarOptions] = useState<CarSearch[]>([]);

  const defaultDate = getDefaultDateForm(highwayPass?.startDate, highwayPass?.endDate);

  const defaultValues: Partial<HighwayPassFormBody> = {
    car: highwayPass?.car,
    startDate: !!isCarOverview ? defaultDate : highwayPass?.startDate,
    endDate: !isCarOverview ? highwayPass?.endDate : undefined,
    nr: !isCarOverview ? highwayPass?.nr : undefined,
    prevEndDate: (!!isCarOverview && highwayPass?.endDate) || defaultDate,
  };

  const getTitle = (startDate?: string, endDate?: string): string => {
    const today = new Date().toISOString();
    if (startDate && startDate <= today && (!endDate || endDate >= today)) {
      return i18n.t.common.currentHighwayPass;
    }
    return i18n.t.common.lastHighwayPass;
  };

  return (
    <Form<HighwayPassFormBody>
      defaultValues={defaultValues}
      submitText={i18n.t.common.save}
      disabled={isDisabled || isLoading}
      onSubmit={() => onSubmit}
      itemCreationInfo={highwayPass}
    >
      {({ register, formState: { errors }, control, reset, watch, setValue }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [highwayPass]);

        return (
          <FormFieldsContainer>
            {isCarOverview && (
              <>
                <FormField label={getTitle(highwayPass?.startDate, highwayPass?.endDate)}>
                  <Input disabled value={highwayPass?.nr} />
                </FormField>
                <FormField label={i18n.t.common.from}>
                  <Input disabled value={highwayPass?.startDate ?? ""} />
                </FormField>
                <Controller
                  name="prevEndDate"
                  control={control}
                  rules={{
                    required: true,
                    validate: (value) => {
                      if (value) {
                        if (value === highwayPass?.startDate) {
                          return i18n.t.common.endDateCantMatchStartDate;
                        }
                        if (highwayPass && value < highwayPass.startDate) {
                          return i18n.t.common.endDateBeforeStartDate;
                        }
                      }
                      return undefined;
                    },
                  }}
                  render={({ fieldState }) => (
                    <FormField label={i18n.t.common.to} error={fieldState.error}>
                      {({ isInvalid }) => (
                        <DatePicker
                          onChange={(v) => !!v && setValue("prevEndDate", v)}
                          isInvalid={isInvalid}
                          value={watch("prevEndDate")}
                          disabled={isDisabled}
                        />
                      )}
                    </FormField>
                  )}
                />
              </>
            )}
            {!!allowSelectCar && (
              <Controller
                name="car"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.cars.singular} error={fieldState.error}>
                    {({ labelId, isInvalid }) => {
                      const searchTerm = useRef("");
                      const aborter = useRef(new AbortController());

                      const loadOptions = createLoadOptions<CarSearch, CarField>(aborter, searchTerm, carsApi.getCars, {
                        fields: CAR_FIELDS,
                      });

                      useEffect(() => {
                        loadOptions(searchTerm.current).then(setDefaultCarOptions);
                      }, []);

                      return (
                        <AsyncSelect
                          menuPosition="fixed"
                          {...field}
                          inputId={labelId}
                          defaultOptions={defaultCarOptions}
                          loadOptions={loadOptions}
                          isDisabled={!!highwayPass}
                          placeholder={`${i18n.t.common.select}...`}
                          getOptionLabel={(x) =>
                            x.currentPlateNumber ?? x.lastPlateNumber ?? x.vehicleIdentificationNumber ?? ""
                          }
                          getOptionValue={(x) => x.id ?? ""}
                          styles={reactSelectStyles<Partial<Car>>(isInvalid)}
                        />
                      );
                    }}
                  </FormField>
                )}
              />
            )}
            <FormField label={!!isCarOverview ? i18n.t.highwayPasses.singular : i18n.t.common.number} error={errors.nr}>
              {({ labelId, isInvalid }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading}
                  {...register("nr", {
                    required: true,
                  })}
                />
              )}
            </FormField>
            <Controller
              name="startDate"
              control={control}
              rules={{
                required: true,
                validate: (value) => {
                  if (!!isCarOverview) {
                    if (value) {
                      const prevEndDate = watch("prevEndDate");
                      if (highwayPass) {
                        if (prevEndDate) {
                          if (value < prevEndDate) {
                            return i18n.t.common.startDateFallsInCurrentAssignmentPeriod;
                          }
                          // } else {
                          //   if (value >= prevStartDate) {
                          //     return i18n.t.common.startDateMustBeBeforeCurrentAssignmentStartDate;
                          //   }
                        }
                      }
                    }
                  }
                  return undefined;
                },
              }}
              render={({ fieldState }) => (
                <FormField label={i18n.t.common.startDate} error={fieldState.error}>
                  {({ isInvalid }) => (
                    <DatePicker
                      onChange={(v) => !!v && setValue("startDate", v)}
                      isInvalid={isInvalid}
                      disabled={isDisabled || isLoading}
                      value={watch("startDate")}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="endDate"
              control={control}
              rules={{
                validate: (value) => {
                  if (value) {
                    const startDate = watch("startDate");
                    if (value === startDate) {
                      return i18n.t.common.endDateCantMatchStartDate;
                    }
                    if (value < startDate) {
                      return i18n.t.common.endDateBeforeStartDate;
                    }

                    const d = new Date(value);
                    4;
                    if (d.getFullYear() < 1000) {
                      return i18n.t.common.invalidDate;
                    }
                  }
                  return undefined;
                },
              }}
              render={({ fieldState }) => (
                <FormField label={i18n.t.common.endDate} error={fieldState.error} isOptional>
                  {({ isInvalid }) => (
                    <DatePicker
                      onChange={(v) => setValue("endDate", v!)}
                      isInvalid={isInvalid}
                      disabled={isDisabled || isLoading}
                      value={watch("endDate")}
                      clearable
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default HighwayPassForm;
